// § 4 · Data
// "What did the data become?"
//
// Sub-tabs: Live (raw events stream), BigQuery (per-table rows), Files
// (raw uploads), Errors (failed runs), Replays (replay history). Tab
// state syncs to URL `?dt=<tab>` so deep links work.

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { DataHubEvent, Replay, Source, TriggerRun } from '@datahub/shared';
import { getEventsPreview, listSourceErrors, listReplays } from '../../api';
import { SectionShell, EmptyState } from './SectionShell';
import { ReplayDialog } from '../dialogs/ReplayDialog';
import { FingerprintGroupsV2 } from './FingerprintGroupsV2';
import { BqDatasetPanelV2 } from './BqDatasetPanelV2';

type DataTab = 'live' | 'groups' | 'errors' | 'replays' | 'files' | 'bq';

const TAB_LABELS: Record<DataTab, string> = {
  live: 'Live stream',
  groups: 'Schema groups',
  errors: 'Errors',
  replays: 'Replays',
  files: 'Files',
  bq: 'BigQuery',
};

export function DataExplorerV2({ source }: { source: Source }): React.ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  // Default landing tab: BigQuery. Vision (2026-05-09): operator opens
  // a source and immediately sees the data — directly through BQ — with
  // inline edit-to-view affordances. The Live tab is a debug/raw stream
  // surface; the BQ tab is the working analytics surface.
  const initialTab = (searchParams.get('dt') as DataTab) ?? 'bq';
  const [tab, setTab] = useState<DataTab>(initialTab);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<DataTab>).detail;
      if (detail) setTab(detail);
    };
    window.addEventListener('v2-data-tab', handler);
    return () => window.removeEventListener('v2-data-tab', handler);
  }, []);

  function pickTab(t: DataTab) {
    setTab(t);
    setSearchParams(p => { p.set('dt', t); return p; });
  }

  const [showReplay, setShowReplay] = useState(false);

  return (
    <SectionShell
      id="data"
      title="Data"
      subtitle="What did the data become"
      headerExtra={
        <button className="btn sm ghost" onClick={() => setShowReplay(true)}>↻ replay</button>
      }
    >
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--line)', marginBottom: 14, flexWrap: 'wrap' }}>
        {(Object.keys(TAB_LABELS) as DataTab[]).map(t => (
          <button
            key={t}
            onClick={() => pickTab(t)}
            style={{
              padding: '8px 14px', fontSize: 12.5,
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: tab === t ? 'var(--ink)' : 'var(--ink-3)',
              borderBottom: `2px solid ${tab === t ? 'var(--accent)' : 'transparent'}`,
              marginBottom: -1,
              fontWeight: tab === t ? 500 : 400,
            }}
          >{TAB_LABELS[t]}</button>
        ))}
      </div>

      {tab === 'live' && <LivePanel sourceId={source.source_id} />}
      {tab === 'groups' && <FingerprintGroupsV2 source={source} />}
      {tab === 'errors' && <ErrorsPanel sourceId={source.source_id} />}
      {tab === 'replays' && <ReplaysPanel sourceId={source.source_id} />}
      {tab === 'files' && (
        <EmptyState
          icon="📎"
          title="Files sub-panel — coming to v2"
          body="Until then, switch to legacy view to inspect raw uploads. This sub-tab will surface the same data with the v2 chrome shortly."
        />
      )}
      {tab === 'bq' && <BqDatasetPanelV2 sourceId={source.source_id} />}

      {showReplay && (
        <ReplayDialog
          sourceId={source.source_id}
          views={[]}
          triggers={[]}
          onCancel={() => setShowReplay(false)}
          onLaunched={() => setShowReplay(false)}
        />
      )}
    </SectionShell>
  );
}

function LivePanel({ sourceId }: { sourceId: string }): React.ReactElement {
  const [events, setEvents] = useState<DataHubEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    function load() {
      getEventsPreview(sourceId, 30)
        .then(e => { if (alive) { setEvents(e); setLoading(false); } })
        .catch(() => { if (alive) setLoading(false); });
    }
    load();
    const t = setInterval(load, 5000);
    return () => { alive = false; clearInterval(t); };
  }, [sourceId]);

  if (loading) return <div style={{ padding: 20, textAlign: 'center', color: 'var(--ink-3)' }}>Loading…</div>;
  if (events.length === 0) {
    return <EmptyState icon="◌" title="No events yet" body="Send a test from Inputs › Webhook to see your first event here." />;
  }
  // Pre-build lineage maps so each row can show "produced N children"
  // and "child of <triggerName>" without an extra API call.
  // child_count[parent_event_id] = N
  // child_origins[parent_event_id] = Set<origin.id>  (which trigger(s) produced derivatives)
  const childCount: Record<string, number> = {};
  const childOrigins: Record<string, Set<string>> = {};
  for (const ev of events) {
    if (ev.parent_event_id) {
      childCount[ev.parent_event_id] = (childCount[ev.parent_event_id] ?? 0) + 1;
      if (ev.origin.kind === 'trigger' && ev.origin.id) {
        if (!childOrigins[ev.parent_event_id]) childOrigins[ev.parent_event_id] = new Set();
        childOrigins[ev.parent_event_id]!.add(ev.origin.id);
      }
    }
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 560, overflowY: 'auto' }}>
      {events.map(e => {
        const isInput = e.origin.kind === 'input';
        const dotColor = isInput ? 'oklch(0.62 0.18 145)' : 'oklch(0.65 0.16 60)';
        const chipBg  = isInput ? 'oklch(0.95 0.06 145 / 0.5)' : 'oklch(0.95 0.06 60 / 0.5)';
        const chipFg  = isInput ? 'oklch(0.45 0.14 145)' : 'oklch(0.50 0.14 60)';
        const kids = childCount[e.event_id] ?? 0;
        const kidOrigins = childOrigins[e.event_id];
        return (
          <details key={e.event_id} style={{
            background: 'var(--bg)',
            border: '1px solid var(--line)',
            borderLeft: `3px solid ${dotColor}`,
            borderRadius: 6,
          }}>
            <summary style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <code style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)' }}>{new Date(e.received_at).toLocaleTimeString()}</code>
              <span title={isInput ? `INPUT · arrived via ${e.origin.id ?? '(unspecified)'}` : `DERIVED · produced by trigger ${e.origin.id ?? '?'} (depth ${e.derivation_depth})`}
                    style={{ fontSize: 10, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: chipFg, padding: '2px 7px', background: chipBg, borderRadius: 3, fontWeight: 600 }}>
                {isInput ? '← INPUT' : '→ DERIVED'}
              </span>
              <span style={{ fontSize: 10.5, fontFamily: 'var(--mono)', color: 'var(--ink-3)' }}>
                {e.origin.id ?? ''}
                {!isInput && e.derivation_depth > 1 && <> · d={e.derivation_depth}</>}
              </span>
              {kids > 0 && (
                <span title={`This event produced ${kids} derivative${kids === 1 ? '' : 's'}${kidOrigins ? ' via ' + [...kidOrigins].join(', ') : ''}`}
                      style={{ fontSize: 10, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--accent)', padding: '2px 6px', background: 'var(--accent-soft)', border: '1px solid var(--accent-line)', borderRadius: 3 }}>
                  ⤳ {kids} deriv
                </span>
              )}
              <span style={{ flex: 1, fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--ink-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 100 }}>
                {Object.keys(e.payload ?? {}).slice(0, 5).join(', ')}{Object.keys(e.payload ?? {}).length > 5 ? '…' : ''}
              </span>
            </summary>
            <pre style={{ padding: 12, background: 'var(--bg-sunken)', margin: 0, fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-2)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', borderTop: '1px solid var(--line)', borderRadius: '0 0 6px 6px' }}>
              {JSON.stringify(e.payload, null, 2)}
            </pre>
          </details>
        );
      })}
    </div>
  );
}

function ErrorsPanel({ sourceId }: { sourceId: string }): React.ReactElement {
  const [runs, setRuns] = useState<TriggerRun[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    listSourceErrors(sourceId, 50)
      .then(r => { if (alive) { setRuns(r); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [sourceId]);

  if (loading) return <div style={{ padding: 20, textAlign: 'center', color: 'var(--ink-3)' }}>Loading…</div>;
  if (runs.length === 0) {
    return <EmptyState icon="✓" title="No errors" body="Failed trigger runs from the last 24 hours appear here. Currently none." />;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {runs.map(r => (
        <div key={r.run_id} style={{ padding: '8px 12px', background: 'var(--bg)', border: '1px solid var(--danger)', borderLeft: '3px solid var(--danger)', borderRadius: 6 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
            <code style={{ fontSize: 11, color: 'var(--ink-3)' }}>{new Date(r.ran_at).toLocaleString()}</code>
            <code style={{ fontSize: 11, color: 'var(--ink-3)' }}>trigger {r.trigger_id.slice(0, 12)}…</code>
          </div>
          {r.error && <div style={{ fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--danger)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{r.error}</div>}
        </div>
      ))}
    </div>
  );
}

function ReplaysPanel({ sourceId }: { sourceId: string }): React.ReactElement {
  const [replays, setReplays] = useState<Replay[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    listReplays(sourceId)
      .then(r => { if (alive) { setReplays(r); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [sourceId]);

  if (loading) return <div style={{ padding: 20, textAlign: 'center', color: 'var(--ink-3)' }}>Loading…</div>;
  if (replays.length === 0) {
    return <EmptyState icon="↻" title="No replays yet" body="Replays re-run triggers over a historical event range. Useful after creating a new trigger that needs to backfill." />;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {replays.map(r => (
        <div key={r.replay_id} style={{ padding: '8px 12px', background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 6 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <code style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)' }}>{r.replay_id}</code>
            <span style={{ fontSize: 11.5, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.06em', color: r.status === 'done' ? 'oklch(0.62 0.18 145)' : r.status === 'failed' ? 'var(--danger)' : 'var(--ink-3)' }}>{r.status}</span>
            <span style={{ fontSize: 11.5, color: 'var(--ink-2)' }}>{r.events_replayed ?? 0} events replayed</span>
            <span style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 'auto' }}>{new Date(r.created_at).toLocaleString()}</span>
          </div>
        </div>
      ))}
    </div>
  );
}
